import { State } from '@meraki-internal/state';
import { APIClient } from '@innerhive-internal/innerhive-api-client';
import { buildAgencyDigest, buildCaseloadFigures, IAgencyCaseloadFigures, IUnreadableClient } from '@innerhive-internal/innerhive-common';
import { CareMapState } from '../caremap/CareMapState';
import { IInsightSummary } from '../insights/IInsight';
import { Logger } from '../support/Logger';
import { BufferedEventBus } from '../utils/BufferedEventBus';
import { IResourceChangedSocketMessage, IWebSocketsConnected } from '../web-sockets/SocketState';
import { ORGANIZATION_REFRESH_DELAY_MS, OrganizationsState } from './OrganizationsState';

export interface IClientInsight {
    insight: IInsightSummary;
    careMapId: string;
    clientName: string;
}

/** What an organization's caseload link returns. */
export interface IOrganizationCaseload extends IAgencyCaseloadFigures {
    /** Left out of every figure, each with the reason it could not be read. */
    unreadableClients: IUnreadableClient[];
    /** Every fully readable client's outstanding insights, most urgent first. */
    clientInsights: IClientInsight[];
}

const caseloadOfNobody = (unreadableClients: IUnreadableClient[]): IOrganizationCaseload => ({
    ...buildCaseloadFigures(buildAgencyDigest({ findings: [], clients: [], now: new Date(), unreadableCount: unreadableClients.length })),
    unreadableClients,
    clientInsights: []
});

// The caseload behind the agency home: the Insights tab's figures and the Clients tab's outstanding
// insights, read together in the one request the server computes them in.
export class OrganizationInsightsState extends State<Record<string, never>> {
    static inject = () => [OrganizationsState, CareMapState, APIClient, Logger, BufferedEventBus];
    constructor(
        private orgState: OrganizationsState,
        private careMapState: CareMapState,
        private apiClient: APIClient,
        private logger: Logger,
        private events: BufferedEventBus
    ) {
        super({});

        // A change of who holds a client changes whose words the reader may see, so a caseload follows
        // it, and catches up on any change missed while the socket was down.
        this.events.on('web-sockets.message', this.onWebsocketMessage);
        this.events.on('web-sockets.connected', this.onWebsocketConnected);
    }

    private pendingRefresh?: ReturnType<typeof setTimeout>;

    // The tabs showing the caseload. Every read files an audit row per client the reader is shown, so a
    // caseload nobody is looking at is marked stale rather than read in the background.
    private viewers = 0;
    private stale = false;

    /** Registers a screen showing the caseload; call what it returns when the screen goes. */
    watch = (): (() => void) => {
        this.viewers++;
        return () => {
            this.viewers--;
        };
    };

    private onWebsocketMessage = (message?: IResourceChangedSocketMessage) => {
        const resource = message?.resourceToRefresh?.toLowerCase();
        const caseloadHref = this.loadedOrgId && this.orgState.getOrganization(this.loadedOrgId)?.links.caseload?.href;
        if (resource && caseloadHref && resource === caseloadHref.toLowerCase()) {
            this.caseloadChanged();
        }
    };

    private onWebsocketConnected = (connected?: IWebSocketsConnected) => {
        if (connected?.mayHaveMissedMessages) {
            this.caseloadChanged();
        }
    };

    private caseloadChanged = () => {
        // A caseload being read for the first time counts: that read may predate the change.
        if (!this.loadedOrgId || !(this.hasLoaded || this.inFlight)) {
            return;
        }
        if (this.viewers <= 0) {
            this.stale = true;
            return;
        }
        const orgId = this.loadedOrgId;
        clearTimeout(this.pendingRefresh);
        this.pendingRefresh = setTimeout(async () => {
            this.pendingRefresh = undefined;
            if (this.viewers <= 0) {
                this.stale = true;
                return;
            }
            try {
                await this.reload(orgId);
            } catch (err) {
                // What is on screen stays until the next message or reconnect reads it again.
                this.logger.error(err, { context: 'OrganizationInsightsState could not refresh the caseload', orgId });
            }
        }, ORGANIZATION_REFRESH_DELAY_MS);
    };

    private caseload?: IOrganizationCaseload;

    // Whether what is held stands in for a read that failed, which the next load replaces rather than keeps.
    private lastReadFailed = false;

    private loadedOrgId?: string;
    private inFlight?: Promise<void>;

    // Which read is the current one. Two can overlap whenever the organization changes or a retry
    // starts, and the one that finishes last is not necessarily the one that should be on screen.
    private generation = 0;

    hasLoaded = false;

    /** Absent until the caseload has been read. */
    getFigures = (): IAgencyCaseloadFigures | undefined => this.caseload;

    getUnreadableClients = (): IUnreadableClient[] => [...(this.caseload?.unreadableClients ?? [])];

    /**
     * Read the caseload again, for the retry a failed client offers.
     *
     * Goes back to the server where load() would hand back what it already has. Deliberately leaves
     * `hasLoaded` alone: clearing it would drop the whole tab to its skeleton for the length of the
     * retry, and leave it there for good if the retry threw.
     */
    reload = async (orgId: string) => {
        // Joined rather than raced: the reader pressing retry is asking for one fresh answer.
        if (this.inFlight) {
            await this.inFlight;
        }

        // The reader may have left for another organization while that one finished, and the
        // generation counter only knows which call is newest, not which caseload is wanted.
        if (this.loadedOrgId !== orgId) {
            return;
        }

        await this.runLoad(orgId);
    };

    load = async (orgId: string) => {
        if (this.inFlight && this.loadedOrgId === orgId) {
            await this.inFlight;
            // A change that arrived while nobody was looking may already have outdated what that read returned.
            if (this.stale && this.loadedOrgId === orgId) {
                await this.runLoad(orgId);
            }
            return;
        }

        // A completed read is kept, not repeated, so a reader moving between the agency tabs does not
        // have the caseload recomputed every time they come back. A failed or stale one is read again.
        if (this.hasLoaded && this.loadedOrgId === orgId && !this.lastReadFailed && !this.stale) {
            return;
        }

        // A different organization's findings are not a stale version of this one's, they are the
        // wrong data. Clearing before the fetch, and publishing the clear, means the tab shows its
        // loading state rather than pairing one org's caseload with another's findings.
        if (this.loadedOrgId !== orgId) {
            this.hasLoaded = false;
            this.caseload = undefined;
            this.setState({});
        }

        await this.runLoad(orgId);
    };

    private runLoad = async (orgId: string) => {
        this.loadedOrgId = orgId;
        this.stale = false;
        const generation = ++this.generation;
        const thisLoad = this.loadInternal({ orgId, generation });
        this.inFlight = thisLoad;

        try {
            await thisLoad;
        } finally {
            // Only the current load may clear the marker. A superseded one clearing it would let the
            // next call start a second read for an organization already being read.
            if (this.inFlight === thisLoad) {
                this.inFlight = undefined;
            }
        }
    };

    private loadInternal = async ({ orgId, generation }: { orgId: string; generation: number }) => {
        const link = this.orgState.getOrganization(orgId)?.links.caseload;
        let caseload: IOrganizationCaseload;
        let failed = false;

        if (!link) {
            // A member the organization offers no caseload, such as one of its own clients, has none to read.
            caseload = caseloadOfNobody([]);
        } else {
            try {
                caseload = await this.apiClient.get(link);
            } catch (err) {
                this.logger.error(err, { context: 'OrganizationInsightsState could not read the caseload', orgId });
                // A retry that fails keeps what the reader already had rather than replacing it with
                // nothing read at all.
                caseload = this.caseload ?? await this.unreadCaseload(orgId);
                failed = true;
            }
        }

        if (this.generation !== generation) {
            return;
        }

        this.caseload = caseload;
        this.lastReadFailed = failed;
        this.hasLoaded = true;
        this.setState({});
    };

    // The reader's caseload named as failed reads, so a caseload that could not be read at all
    // offers the same retry a single failed client does, rather than an error page in place of the
    // agency home.
    private unreadCaseload = async (orgId: string): Promise<IOrganizationCaseload> => {
        if (this.orgState.getOrganizationCareMaps(orgId).length === 0) {
            try {
                await this.orgState.loadOrganizationCareMaps(orgId);
            } catch (err) {
                // Nobody to name then, but the next load reads both again.
                this.logger.error(err, { context: 'OrganizationInsightsState could not read the roster to name its clients', orgId });
            }
        }

        // The whole roster: a caseload that could be read names every client, either read or with the
        // reason it could not be.
        const unreadableClients: IUnreadableClient[] = this.orgState.getOrganizationCareMaps(orgId)
            .map(careMap => ({
                careMapId: careMap.careMapId,
                clientName: this.careMapState.getPatientFullName(careMap) || 'Unnamed client',
                reason: 'request-failed'
            }));
        unreadableClients.sort((a, b) => a.clientName.localeCompare(b.clientName));

        return caseloadOfNobody(unreadableClients);
    };

    /** In the order the server ranked them: most urgent first, then most recently noticed. */
    getAttentionItems = (): IClientInsight[] => [...(this.caseload?.clientInsights ?? [])];

    // What one client has outstanding, most urgent first. The caseload reads by client rather than by
    // finding, so a client's observations are shown under their own name, and the one it leads with
    // is the first of these.
    getItemsForCareMap = (careMapId: string): IClientInsight[] => {
        return this.getAttentionItems().filter(entry => entry.careMapId === careMapId);
    };

    // The most recent evidence behind anything outstanding for this client, shown on their row so
    // the reader can tell a fresh flag from a stale one.
    getLatestActivity = (careMapId: string): string | undefined => {
        const items = this.getItemsForCareMap(careMapId);
        if (items.length === 0) {
            return undefined;
        }
        return items.map(entry => entry.insight.dateRangeTo).sort().pop();
    };
}
